import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { ListSkeleton } from '@/components/ui/skeletons'
import { TableCheckbox } from '@/components/ui/table-checkbox'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { TruncatedLabel } from '@/components/ui/truncated-label'
import { cn } from '@/lib/utils'
import { Fragment, type KeyboardEvent, type ReactNode, type RefObject, type UIEvent, useMemo, useState } from 'react'

export interface OptionListOption {
	value: string
	label: string
	disabled?: boolean
	/** Shown as a tooltip on a disabled row. */
	disabledReason?: ReactNode
	/** Heading to file this option under. Options without one render first, above any heading. */
	group?: string
	/** Rendered before the label. Pre-rendered by the caller so this stays free of icon plumbing. */
	icon?: ReactNode
}

const NAVIGATION_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End'])

/** A picked row is filled. Hover and keyboard highlight fill at half strength, so the two stay apart. */
export const optionRowClass = (picked: boolean) =>
	picked ? 'bg-accent data-[selected=true]:bg-accent' : 'data-[selected=true]:bg-accent/50'

export const OPTION_GROUP_CLASS = '[&_[cmdk-group-items]]:space-y-0.5'

// Keyboard highlighting moves no focus and raises no tooltip, so the highlighted row unfolds instead.
const UNFOLD_ON_KEYBOARD_HIGHLIGHT =
	'group-data-[keyboard-nav=true]/list:group-data-[selected=true]/option:whitespace-normal group-data-[keyboard-nav=true]/list:group-data-[selected=true]/option:wrap-break-word'

export interface OptionListSearch {
	value: string
	onChange: (value: string) => void
	placeholder?: string
	inputRef?: RefObject<HTMLInputElement | null>
	testId?: string
	clearTestId?: string
	clearLabel?: string
}

export interface OptionListProps {
	/** Accessible name for the list, e.g. `Values for Model`. */
	label: string
	options: OptionListOption[]
	values: string[]
	mode: 'single' | 'multi'
	/** Blocks picking anything new while still letting a selected value be removed. */
	atCap?: boolean
	onSelect: (value: string, extendRange: boolean) => void
	search?: OptionListSearch
	/** Bulk actions above the rows, typically a `SelectionHeader`. */
	header?: ReactNode
	/** A row above the options, typically an offer to use whatever was typed. */
	leadingRow?: ReactNode
	/** A row below the options, for a choice that is not one of them. */
	trailingRow?: ReactNode
	/** Below the rows, typically a spinner while the next page loads. */
	footer?: ReactNode
	loading?: boolean
	emptyMessage?: ReactNode
	onScroll?: (event: UIEvent<HTMLDivElement>) => void
	/** Runs before cmdk's own key handling, with the value of the highlighted row. */
	onKeyDown?: (event: KeyboardEvent<HTMLDivElement>, highlighted: string | undefined) => void
	listClassName?: string
	/** Total matches upstream, when only part of the list is loaded. */
	totalCount?: number
	optionTestId?: (value: string) => string
	optionReasonTestId?: (value: string) => string
	/** Written to each row so the highlighted value can be read back off the DOM. A custom row sets it too. */
	valueAttribute?: string
}

/**
 * The body of an option list: search, rows, bulk header, empty and loading states. It owns no
 * trigger and no selection state, so a filter value panel and a form control can share it.
 */
export function OptionList({
	label,
	options,
	values,
	mode,
	atCap,
	onSelect,
	search,
	header,
	leadingRow,
	trailingRow,
	footer,
	loading,
	emptyMessage,
	onScroll,
	onKeyDown,
	listClassName,
	totalCount,
	optionTestId,
	optionReasonTestId,
	valueAttribute = 'data-option-value',
}: OptionListProps) {
	const single = mode === 'single'
	const [keyboardNav, setKeyboardNav] = useState(false)

	const groups = useMemo(() => {
		const byHeading = new Map<string, OptionListOption[]>()
		for (const option of options) {
			const heading = option.group ?? ''
			const existing = byHeading.get(heading)
			if (existing) {
				existing.push(option)
			} else {
				byHeading.set(heading, [option])
			}
		}
		// The leading row belongs above every heading, so it needs an unheaded bucket to sit in even
		// when every option carries a group.
		if ((leadingRow || trailingRow) && !byHeading.has('')) {
			byHeading.set('', [])
		}
		const entries = [...byHeading]
		const ungrouped = entries.findIndex(([heading]) => heading === '')
		if (ungrouped > 0) {
			entries.unshift(...entries.splice(ungrouped, 1))
		}
		return entries
	}, [options, leadingRow, trailingRow])

	return (
		<Command
			shouldFilter={false}
			className='group/list'
			data-keyboard-nav={keyboardNav}
			label={label}
			onKeyDown={event => {
				setKeyboardNav(NAVIGATION_KEYS.has(event.key))
				// cmdk reports its highlight only when its value is controlled, which would re-render every row
				// on each hover. The highlighted row is marked in the DOM either way.
				const highlighted = event.currentTarget
					.querySelector('[cmdk-item][data-selected="true"]')
					?.getAttribute(valueAttribute)
				onKeyDown?.(event, highlighted ?? undefined)
			}}
			onPointerMove={() => setKeyboardNav(false)}>
			{search && (
				<CommandInput
					ref={search.inputRef}
					value={search.value}
					onValueChange={search.onChange}
					placeholder={search.placeholder}
					data-testid={search.testId}
					clearTestId={search.clearTestId}
					clearLabel={search.clearLabel}
					onClear={() => {
						search.onChange('')
						search.inputRef?.current?.focus()
					}}
				/>
			)}
			{header}
			<CommandList className={listClassName} onScroll={onScroll} aria-setsize={totalCount}>
				{loading ? (
					<ListSkeleton count={5} className='p-2' />
				) : (
					<>
						<CommandEmpty>{emptyMessage}</CommandEmpty>
						{groups.map(([heading, groupOptions], groupIndex) => (
							<CommandGroup key={heading} heading={heading || undefined} className={OPTION_GROUP_CLASS}>
								{groupIndex === 0 && leadingRow}
								{groupOptions.map(option => {
									const isSelected = values.includes(option.value)
									const blocked = option.disabled || (atCap && !isSelected)
									const item = (
										<CommandItem
											key={option.value}
											value={option.value}
											onSelect={() => onSelect(option.value, false)}
											onClickCapture={event => {
												if (!event.shiftKey) {
													return
												}
												event.preventDefault()
												event.stopPropagation()
												onSelect(option.value, true)
											}}
											// cmdk derives aria-disabled and data-disabled from this prop and overwrites the
											// attribute, so it has to be set here. It also drops the click handler and skips
											// the row when navigating by keyboard.
											disabled={blocked}
											{...{ [valueAttribute]: option.value }}
											data-testid={optionTestId?.(option.value)}
											className={cn(
												'group/option flex items-center gap-2 cursor-pointer',
												optionRowClass(isSelected),
												// cmdk's disabled styling removes pointer events, taking the tooltip on a
												// disabled row with it. The click handler is already gone, so this only
												// restores hover.
												blocked && 'cursor-not-allowed opacity-50 pointer-events-auto',
											)}>
											{!single && <TableCheckbox checked={isSelected} readOnly className='shrink-0' />}
											{option.icon}
											{option.disabledReason ? (
												<span className='text-body truncate'>{option.label}</span>
											) : (
												<TruncatedLabel
													label={option.label}
													className={cn('text-body', UNFOLD_ON_KEYBOARD_HIGHLIGHT)}
													testId={optionTestId?.(option.value) ? `${optionTestId(option.value)}-full-label` : undefined}
												/>
											)}
										</CommandItem>
									)
									return option.disabledReason ? (
										<Tooltip key={option.value}>
											{/* The trigger wraps a disabled row in an inline-flex span that would shrink to the label. */}
											<TooltipTrigger asChild className='w-full'>
												{item}
											</TooltipTrigger>
											<TooltipContent data-testid={optionReasonTestId?.(option.value)}>
												{option.disabledReason}
											</TooltipContent>
										</Tooltip>
									) : (
										<Fragment key={option.value}>{item}</Fragment>
									)
								})}
								{groupIndex === groups.length - 1 && trailingRow}
							</CommandGroup>
						))}
						{footer}
					</>
				)}
			</CommandList>
		</Command>
	)
}
